if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
    }

    init() {
      super.init();
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.enableSliderLooping = true;
      this.autoplayButtonQuerySelector = '.slideshow__autoplay';
      this.autoPlayPausedClass = 'slideshow__autoplay--paused';
  
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
  
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      window.addEventListener('resize', this.syncAdaptSlideHeight.bind(this));
      this.setSlideVisibility();
      this.syncSlideVideos();
      this.syncAdaptSlideHeight();

      this.querySelectorAll('.banner__price').forEach(ele => {
        ele.innerHTML = Shopify.showPrice(ele.dataset.price * 1);
      });

      this.productBar = this.querySelector('[data-product-bar]');
      this.productPanels = this.productBar
        ? Array.from(this.productBar.querySelectorAll('.slideshow__product-panel'))
        : [];
      this.syncProductBar();

      if (!this.sliderControlWrapper) return;
    }

    // Shows the product panel whose data-slide-index matches the active slide, and
    // collapses the bar entirely on slides with no product. Panels carry a PBar- id
    // prefix on purpose: SliderComponent collects slides with [id^="Slide-"], so a
    // Slide- prefix here would register them as extra slides and break paging.
    syncProductBar() {
      if (!this.productBar || !this.productPanels.length) return;

      let activePanel = null;
      this.productPanels.forEach((panel) => {
        const isActive = Number(panel.dataset.slideIndex) === this.currentPage;
        panel.toggleAttribute('data-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.querySelectorAll('a').forEach((link) => {
          if (isActive) {
            link.removeAttribute('tabindex');
          } else {
            link.setAttribute('tabindex', '-1');
          }
        });
        if (isActive) activePanel = panel;
      });

      this.productBar.classList.toggle('slideshow__product-bar--empty', !activePanel);
    }
  
    onButtonClick(event) {
      super.onButtonClick(event);
      const isFirstSlide = this.currentPage === 1;
      const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
  
      if (!isFirstSlide && !isLastSlide) return;
  
      if (isFirstSlide && event.currentTarget.name === 'previous') {
        this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else if (isLastSlide && event.currentTarget.name === 'next') {
        this.slideScrollPosition = 0;
      }
      this.slider.scrollTo({
        left: this.slideScrollPosition
      });
  
    }
  
    update() {
      super.update();
      const activeSlide = this.querySelector('.slideshow__slide.active');
      if(activeSlide) activeSlide.classList.remove('active');
      this.querySelector(`.slideshow__slide:nth-child(${this.currentPage})`).classList.add('active');
      this.syncSlideVideos();
      this.syncAdaptSlideHeight();
      this.syncProductBar();
      if(this.prevButton) {
        this.prevButton.removeAttribute('disabled');
      }
    }

    initPages() {
      super.initPages();
      this.syncAdaptSlideHeight();
    }

    syncSlideVideos() {
      const activeSlide = this.sliderItemsToShow[this.currentPage - 1];
      this.sliderItemsToShow.forEach((slide) => {
        if (slide === activeSlide) {
          slide.querySelectorAll('deferred-media[data-autoplay]').forEach((media) => {
            if (typeof media.loadContent !== 'function') return;
            media.loadContent(false, true);
          });
        } else {
          this.pauseSlideVideos(slide);
        }
      });
    }

    pauseSlideVideos(slide) {
      slide.querySelectorAll('deferred-media').forEach((media) => {
        const mediaElement = media.deferredElement || media.querySelector('video, iframe');
        if (!mediaElement) return;

        if (mediaElement.nodeName === 'VIDEO') {
          mediaElement.pause();
        } else if (mediaElement.classList.contains('js-youtube')) {
          mediaElement.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (mediaElement.classList.contains('js-vimeo')) {
          mediaElement.contentWindow?.postMessage('{"method":"pause"}', '*');
        }

        media.isPlaying = false;
      });
    }

    syncAdaptSlideHeight() {
      if (!this.slider) return;

      if (this.slider.dataset.hasVideoSlide !== 'true') {
        this.slider.style.removeProperty('height');
        return;
      }

      const isMobile = window.innerWidth < 750;
      const shouldAdapt = isMobile
        ? this.slider.dataset.adaptMobile === 'true'
        : this.slider.dataset.adaptDesktop === 'true';
      // Hold ONE height for the whole slideshow so the banner does not resize as
      // it rotates: the video is 1.775 and every image slide is 2.0, a jump of
      // 91px on desktop and 25px on mobile.
      //
      // The reference is an IMAGE slide, not the video. The hero creatives are
      // 1x assets (1440x720 desktop, 390x195 mobile), already undersampled 2x at
      // DPR 2. Locking to the video's taller 1.775 box forced a 12.7% upscale on
      // top of that and visibly softened them. Locking to the image ratio paints
      // them 1:1 and lets the single video cover instead, losing about 11%
      // vertically, which reads far better than six soft banners.
      const referenceSlide = this.slider.querySelector('.slideshow__slide:not(.slideshow__slide--video)')
        || this.sliderItemsToShow?.[this.currentPage - 1];
      const aspectRatio = Number.parseFloat(isMobile
        ? referenceSlide?.dataset.slideMobileAspectRatio || referenceSlide?.dataset.slideAspectRatio
        : referenceSlide?.dataset.slideAspectRatio);

      if (!shouldAdapt || !aspectRatio) {
        this.slider.style.removeProperty('height');
        return;
      }

      this.slider.style.height = `${this.slider.clientWidth / aspectRatio}px`;
    }
  
    setSlideVisibility() {
      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll('a');
        if (index === this.currentPage - 1) {
          if (linkElements.length) linkElements.forEach(button => {
            button.removeAttribute('tabindex');
          });
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
        } else {
          if (linkElements.length) linkElements.forEach(button => {
            button.setAttribute('tabindex', '-1');
          });
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
        }
      });
    }
  
    play() {
      super.play();
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.remove(this.autoPlayPausedClass);
        this.sliderAutoplayButton.querySelector('.slideshow__progress__path').getAnimations().forEach((anim) => {
          anim.cancel();
          anim.play();
        });
      }
    }

    pause() {
      super.pause();
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.add(this.autoPlayPausedClass);
        this.sliderAutoplayButton.querySelector('.slideshow__progress__path').getAnimations().forEach((anim) => {
          anim.pause();
        });
      }
    }
  });
}
